import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency } from '../../lib/i18n';
import {
  ArrowLeft,
  Save,
  Trash2,
  AlertTriangle,
  Plus,
  X,
  Search,
  Package,
  Minus,
  Pencil,
  Wrench,
  User,
  Bike,
  Bell
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'pending', label: 'Pending', labelBn: 'অপেক্ষমান' },
  { value: 'in_progress', label: 'In Progress', labelBn: 'চলমান' },
  { value: 'waiting_parts', label: 'Waiting for Parts/Hold', labelBn: 'পার্টসের জন্য অপেক্ষা/হোল্ড' },
  { value: 'completed', label: 'Completed', labelBn: 'সম্পন্ন' },
  { value: 'delivered', label: 'Delivered', labelBn: 'ডেলিভারি হয়েছে' }
];

const statusFlow = ['pending', 'in_progress', 'waiting_parts', 'completed', 'delivered'];

export default function JobEditPage() {
  const { id, garageSlug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Customer and bike management  
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerBikes, setCustomerBikes] = useState([]);
  const [selectedBikeId, setSelectedBikeId] = useState('');
  const [showNewBike, setShowNewBike] = useState(false);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  
  // Available services and parts for selection
  const [availableServices, setAvailableServices] = useState([]);
  const [availableParts, setAvailableParts] = useState([]);
  const [recentParts, setRecentParts] = useState([]);
  const [partSearch, setPartSearch] = useState('');
  const [editingPartPrice, setEditingPartPrice] = useState(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [editingServicePrice, setEditingServicePrice] = useState(null);
  const [customService, setCustomService] = useState({ name: '', price: '', cost: '' });
  const [showServiceCost, setShowServiceCost] = useState(false);
  const [customPart, setCustomPart] = useState({ name: '', selling_price: '', buying_price: '' });
  const [technicians, setTechnicians] = useState([]);
  const [partCategoryMap, setPartCategoryMap] = useState({});
  
  // Status change confirmation
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [hasInvoice, setHasInvoice] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_id: '',
    bike_id: '',
    bike_make: '',
    bike_model: '',
    bike_year: '',
    bike_registration: '',
    bike_color: '',
    status: '',
    services: [],
    service_prices: {},
    custom_services: [],
    parts_used: [],
    custom_parts: [],
    technician_id: '',
    notes: '',
    expected_delivery: ''
  });

  useEffect(() => {
    fetchData();
    // Fetch consumable category mappings for reminder indicator
    (async () => {
      try {
        const { data } = await axios.get(`${API}/marketing/part-category-map`);
        setPartCategoryMap(data || {});
      } catch { /* silent */ }
    })();
  }, [id]);

  const fetchData = async () => {
    try {
      const [jobRes, servicesRes, usersRes, customersRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/jobs/${id}`),
        axios.get(`${API}/services`),
        axios.get(`${API}/users`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/invoices?job_id=${id}`).catch(() => ({ data: [] }))
      ]);
      
      const jobData = jobRes.data;
      setJob(jobData);
      
      // Check if any invoice exists for this job
      const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];
      setHasInvoice(invoices.some(inv => inv.job_id === id));
      
      // Initialize form data
      setFormData({
        customer_id: jobData.customer_id || '',
        bike_id: jobData.bike_id || '',
        bike_make: jobData.bike_make || '',
        bike_model: jobData.bike_model || '',
        bike_year: jobData.bike_year || '',
        bike_registration: jobData.bike_registration || '',
        bike_color: jobData.bike_color || '',
        status: jobData.status,
        services: jobData.services?.map(s => s.id) || [],
        service_prices: jobData.service_prices || {},
        custom_services: jobData.custom_services || [],
        parts_used: jobData.parts_used || [],
        custom_parts: jobData.custom_parts || [],
        technician_id: jobData.technician_id || '',
        notes: jobData.notes || '',
        expected_delivery: jobData.expected_delivery || ''
      });
      
      setAvailableServices(servicesRes.data);
      setTechnicians(usersRes.data.filter(u => u.role === 'technician' || u.role === 'owner' || u.role === 'manager'));
      setCustomers(customersRes.data);
      
      // Set customer if exists
      if (jobData.customer_id) {
        const customer = customersRes.data.find(c => c.id === jobData.customer_id);
        if (customer) {
          setSelectedCustomer(customer);
          setCustomerSearch(customer.name + ' - ' + customer.phone);
          await fetchCustomerBikes(jobData.customer_id);
        }
      }
      
      // Set bike selection
      if (jobData.bike_id) {
        setSelectedBikeId(jobData.bike_id);
      }
      
      // Load recent parts
      loadRecentParts();
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast({ title: 'Failed to load job', variant: 'destructive' });
      navigate(`${basePath}/jobs`);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentParts = async () => {
    try {
      const recentPartIds = JSON.parse(localStorage.getItem('recentParts') || '[]');
      if (recentPartIds.length > 0) {
        const response = await axios.post(`${API}/parts/batch`, recentPartIds.slice(0, 8));
        const recent = recentPartIds
          .map(id => response.data.find(p => p.id === id))
          .filter(p => p && p.stock_quantity > 0)
          .slice(0, 8);
        setRecentParts(recent);
      }
    } catch (error) {
      console.error('Failed to load recent parts:', error);
    }
  };

  const fetchParts = async (searchTerm) => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      params.append('limit', '24');
      const response = await axios.get(`${API}/parts?${params}`);
      setAvailableParts(response.data.filter(p => p.stock_quantity > 0));
    } catch (error) {
      console.error('Failed to fetch parts:', error);
    }
  };

  const fetchServices = async (searchTerm) => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      const response = await axios.get(`${API}/services?${params}`);
      setAvailableServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
    
    // Clear search and reload all services when selecting
    if (!formData.services.includes(serviceId)) {
      setServiceSearch('');
      fetchServices('');
    }
  };

  const updateServicePrice = (serviceId, newPrice) => {
    const priceNum = parseInt(newPrice) || 0;
    setFormData(prev => ({
      ...prev,
      service_prices: { ...prev.service_prices, [serviceId]: priceNum }
    }));
  };

  const addCustomService = () => {
    if (!customService.name.trim() || !customService.price) {
      toast({ title: language === 'en' ? 'Please enter service name and price' : 'সার্ভিসের নাম এবং মূল্য লিখুন', variant: 'destructive' });
      return;
    }
    const newCustomService = {
      id: `custom_${Date.now()}`,
      name: customService.name,
      price: parseInt(customService.price) || 0,
      cost: parseInt(customService.cost) || 0
    };
    setFormData(prev => ({
      ...prev,
      custom_services: [...(prev.custom_services || []), newCustomService]
    }));
    setCustomService({ name: '', price: '', cost: '' });
  };

  const removeCustomService = (customId) => {
    setFormData(prev => ({
      ...prev,
      custom_services: prev.custom_services.filter(cs => cs.id !== customId)
    }));
  };

  const addCustomPart = () => {
    if (!customPart.name || !customPart.selling_price) {
      toast({ title: language === 'en' ? 'Part name and selling price required' : 'পার্টসের নাম ও বিক্রয় মূল্য আবশ্যক', variant: 'destructive' });
      return;
    }
    const cpId = `cp_${Date.now()}`;
    setFormData(prev => ({
      ...prev,
      custom_parts: [...(prev.custom_parts || []), {
        id: cpId,
        name: customPart.name,
        quantity: 1,
        selling_price: parseFloat(customPart.selling_price) || 0,
        buying_price: parseFloat(customPart.buying_price) || 0
      }]
    }));
    setCustomPart({ name: '', selling_price: '', buying_price: '' });
  };

  const removeCustomPart = (cpId) => {
    setFormData(prev => ({
      ...prev,
      custom_parts: (prev.custom_parts || []).filter(p => p.id !== cpId)
    }));
  };

  const updateCustomPartQuantity = (cpId, delta) => {
    setFormData(prev => ({
      ...prev,
      custom_parts: (prev.custom_parts || [])
        .map(p => p.id === cpId ? { ...p, quantity: Math.max(0, (p.quantity || 1) + delta) } : p)
        .filter(p => p.quantity > 0)
    }));
  };

  const fetchCustomerBikes = async (customerId) => {
    try {
      const response = await axios.get(`${API}/bikes?customer_id=${customerId}`);
      setCustomerBikes(response.data);
    } catch (error) {
      console.error('Failed to fetch bikes:', error);
      setCustomerBikes([]);
    }
  };

  const handleCustomerSearch = async (searchTerm) => {
    setCustomerSearch(searchTerm);
    if (searchTerm.length > 0) {
      // Filter customers locally
      const filtered = customers.filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.includes(searchTerm)
      );
      // Show filtered results
    }
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name || customer.phone);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      bike_id: '',
      bike_make: '',
      bike_model: '',
      bike_year: '',
      bike_registration: '',
      bike_color: ''
    }));
    setSelectedBikeId('');
    setShowNewBike(false);
    await fetchCustomerBikes(customer.id);
  };

  const handleSelectBike = (bike) => {
    setSelectedBikeId(bike.id);
    setFormData(prev => ({
      ...prev,
      bike_id: bike.id,
      bike_make: bike.make || '',
      bike_model: bike.model || '',
      bike_year: bike.year || '',
      bike_registration: bike.registration_number || '',
      bike_color: bike.color || ''
    }));
    setShowNewBike(false);
  };

  const handleNewBikeToggle = () => {
    setShowNewBike(!showNewBike);
    if (!showNewBike) {
      setSelectedBikeId('');
      setFormData(prev => ({
        ...prev,
        bike_id: '',
        bike_make: '',
        bike_model: '',
        bike_year: '',
        bike_registration: '',
        bike_color: ''
      }));
    }
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerBikes([]);
    setSelectedBikeId('');
    setShowNewBike(false);
    setFormData(prev => ({
      ...prev,
      customer_id: '',
      bike_id: '',
      bike_make: '',
      bike_model: '',
      bike_year: '',
      bike_registration: '',
      bike_color: ''
    }));
  };

  const cancelCustomerEdit = () => {
    setIsEditingCustomer(false);
    setCustomerSearch('');
    setShowNewBike(false);
    // Reset to original customer/bike from job
    if (job.customer_id) {
      const customer = customers.find(c => c.id === job.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
        setCustomerSearch(customer.name + ' - ' + customer.phone);
      }
    }
  };

  const getFilteredCustomers = () => {
    if (!customerSearch) return [];
    return customers.filter(c =>
      c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
      c.phone?.includes(customerSearch)
    );
  };

  const formatRegistrationNumber = (value) => {
    // Remove any non-alphanumeric characters except hyphen
    let clean = value.replace(/[^A-Za-z0-9-]/g, '');
    
    // Auto-format to LA-XX-XXXX pattern
    if (clean.length <= 2) return clean.toUpperCase();
    if (clean.includes('-')) {
      // Already has hyphens, just uppercase
      return clean.toUpperCase();
    }
    // Auto-insert hyphens
    if (clean.length <= 4) {
      return clean.slice(0, 2).toUpperCase() + '-' + clean.slice(2).toUpperCase();
    }
    return clean.slice(0, 2).toUpperCase() + '-' + clean.slice(2, 4) + '-' + clean.slice(4, 8);
  };

  const isStatusBackwards = (currentStatus, newStatus) => {
    const currentIndex = statusFlow.indexOf(currentStatus);
    const newIndex = statusFlow.indexOf(newStatus);
    return newIndex < currentIndex;
  };

  const handleStatusChange = (newStatus) => {
    if (isStatusBackwards(formData.status, newStatus)) {
      setPendingStatus(newStatus);
      setShowStatusConfirm(true);
    } else {
      setFormData(prev => ({ ...prev, status: newStatus }));
    }
  };

  const confirmStatusChange = () => {
    setFormData(prev => ({ ...prev, status: pendingStatus }));
    setShowStatusConfirm(false);
    setPendingStatus(null);
  };

  const cancelStatusChange = () => {
    setShowStatusConfirm(false);
    setPendingStatus(null);
  };

  const handleAddPart = (part) => {
    const exists = formData.parts_used.find(p => p.part_id === part.id);
    if (exists) {
      if (exists.quantity < part.stock_quantity) {
        setFormData(prev => ({
          ...prev,
          parts_used: prev.parts_used.map(p =>
            p.part_id === part.id ? { ...p, quantity: p.quantity + 1 } : p
          )
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        parts_used: [...prev.parts_used, {
          part_id: part.id,
          part_name: part.name,
          quantity: 1,
          unit_price: part.selling_price
        }]
      }));
    }

    // Save to recent parts
    const recentPartIds = JSON.parse(localStorage.getItem('recentParts') || '[]');
    const updatedRecent = [part.id, ...recentPartIds.filter(id => id !== part.id)].slice(0, 10);
    localStorage.setItem('recentParts', JSON.stringify(updatedRecent));
    loadRecentParts();
    
    // Clear search
    setPartSearch('');
  };

  const handleUpdatePartQuantity = (partId, quantity) => {
    setFormData(prev => ({
      ...prev,
      parts_used: prev.parts_used.map(p => 
        p.part_id === partId ? { ...p, quantity: Math.max(1, parseInt(quantity) || 1) } : p
      )
    }));
  };

  const handleUpdatePartPrice = (partId, totalPrice) => {
    setFormData(prev => ({
      ...prev,
      parts_used: prev.parts_used.map(p => {
        if (p.part_id === partId) {
          const total = Math.max(0, parseFloat(totalPrice) || 0);
          const unit_price = p.quantity > 0 ? total / p.quantity : 0;
          return { ...p, unit_price };
        }
        return p;
      })
    }));
  };

  const handleRemovePart = (partId) => {
    setFormData(prev => ({
      ...prev,
      parts_used: prev.parts_used.filter(p => p.part_id !== partId)
    }));
  };

  const calculateTotals = () => {
    const servicesTotal = formData.services.reduce((sum, serviceId) => {
      const service = availableServices.find(s => s.id === serviceId);
      const price = formData.service_prices?.[serviceId] ?? service?.price ?? 0;
      return sum + price;
    }, 0);
    
    const customServicesTotal = (formData.custom_services || []).reduce((sum, cs) => sum + (cs.price || 0), 0);
    
    const partsTotal = formData.parts_used.reduce((sum, part) => {
      return sum + (part.quantity * part.unit_price);
    }, 0);

    const customPartsTotal = (formData.custom_parts || []).reduce((sum, p) => sum + (p.quantity * p.selling_price), 0);
    
    return { servicesTotal: servicesTotal + customServicesTotal, partsTotal: partsTotal + customPartsTotal, total: servicesTotal + customServicesTotal + partsTotal + customPartsTotal };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert bike_year to int or null
      const bikeYear = formData.bike_year ? parseInt(formData.bike_year) : null;
      
      await axios.put(`${API}/jobs/${id}`, {
        customer_id: formData.customer_id || null,
        bike_id: formData.bike_id || null,
        bike_make: formData.bike_make || null,
        bike_model: formData.bike_model || null,
        bike_registration: formData.bike_registration || null,
        bike_year: bikeYear,
        bike_color: formData.bike_color || null,
        status: formData.status,
        services: formData.services,
        service_prices: formData.service_prices,
        custom_services: formData.custom_services,
        parts_used: formData.parts_used,
        custom_parts: formData.custom_parts,
        technician_id: formData.technician_id || null,
        notes: formData.notes,
        expected_delivery: formData.expected_delivery || null
      });
      
      toast({ title: language === 'en' ? 'Job updated successfully!' : 'জব সফলভাবে আপডেট হয়েছে!' });
      navigate(`${basePath}/jobs/${id}`);
    } catch (error) {
      console.error('Failed to update job:', error);
      toast({ title: language === 'en' ? 'Failed to update job' : 'জব আপডেট করতে ব্যর্থ', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/jobs/${id}`);
      toast({ title: language === 'en' ? 'Job deleted successfully!' : 'জব সফলভাবে মুছে ফেলা হয়েছে!' });
      navigate(`${basePath}/jobs`);
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast({ 
        title: error.response?.data?.detail || (language === 'en' ? 'Failed to delete job' : 'জব মুছতে ব্যর্থ'), 
        variant: 'destructive' 
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (!job) return null;

  const totals = calculateTotals();
  const isCompleted = job.status === 'completed' || job.status === 'delivered';

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`${basePath}/jobs/${id}`)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-['Barlow_Condensed']">
                {language === 'en' ? 'Edit Job' : 'জব সম্পাদনা'}: {job.job_number}
              </h1>
              <p className="text-slate-500">
                {job.customer_name} • {job.bike_make} {job.bike_model}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={deleting} data-testid="delete-job-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Delete' : 'মুছুন'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    {language === 'en' ? 'Delete Job?' : 'জব মুছে ফেলবেন?'}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {language === 'en' 
                      ? `Are you sure you want to delete job ${job.job_number}? This action cannot be undone and will affect your reports.`
                      : `আপনি কি নিশ্চিত যে আপনি জব ${job.job_number} মুছে ফেলতে চান? এই ক্রিয়াটি পূর্বাবস্থায় ফেরানো যাবে না এবং আপনার রিপোর্টগুলিকে প্রভাবিত করবে।`
                    }
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{language === 'en' ? 'Cancel' : 'বাতিল'}</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-btn"
                  >
                    {deleting ? <Spinner size="sm" /> : (language === 'en' ? 'Delete' : 'মুছুন')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Warning for completed jobs */}
        {isCompleted && (
          <Card className="mb-6 border-yellow-300 bg-yellow-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <p className="text-yellow-800">
                {language === 'en' 
                  ? 'This job is already completed. Changes may affect invoices and reports.'
                  : 'এই জবটি ইতিমধ্যে সম্পন্ন হয়েছে। পরিবর্তনগুলি চালান এবং রিপোর্টকে প্রভাবিত করতে পারে।'
                }
              </p>
            </CardContent>
          </Card>
        )}

        {/* Customer & Bike Info */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {language === 'en' ? 'Customer & Vehicle Details' : 'গ্রাহক ও বাইকের বিবরণ'}
              </CardTitle>
              {!isEditingCustomer && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditingCustomer(true)}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Edit' : 'সম্পাদনা'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isEditingCustomer ? (
              // Read-only view
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Customer' : 'গ্রাহক'}</p>
                  <p className="font-semibold">{job.customer_name}</p>
                  <p className="text-sm text-slate-600">{job.customer_phone}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Vehicle' : 'বাইক'}</p>
                  <p className="font-semibold">{job.bike_make} {job.bike_model}</p>
                  <p className="text-sm text-slate-600">{job.bike_registration || '-'}</p>
                </div>
              </div>
            ) : (
              // Edit mode
              <>
                <div className="flex justify-end mb-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={cancelCustomerEdit}
                  >
                    <X className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Cancel' : 'বাতিল'}
                  </Button>
                </div>
                {/* Customer Section */}
                {!selectedCustomer ? (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        placeholder={language === 'en' ? 'Search customer by name or phone...' : 'নাম বা ফোন দিয়ে গ্রাহক খুঁজুন...'}
                        value={customerSearch}
                        onChange={(e) => handleCustomerSearch(e.target.value)}
                        className="pl-10 h-12"
                      />
                      {getFilteredCustomers().length > 0 && (
                        <div className="absolute top-full left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 z-10 max-h-48 overflow-auto">
                          {getFilteredCustomers().map((customer) => (
                            <button
                              key={customer.id}
                              type="button"
                              onClick={() => handleSelectCustomer(customer)}
                              className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b last:border-b-0"
                            >
                              <p className="font-medium">{customer.name}</p>
                              <p className="text-sm text-slate-500">{customer.phone}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      {language === 'en' 
                        ? 'Leave empty to keep as quick entry' 
                        : 'কুইক এন্ট্রি রাখতে খালি রাখুন'
                      }
                    </p>
                  </>
                ) : (
                  <>
                    {/* Selected Customer Display */}
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-blue-800" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{selectedCustomer.name}</p>
                        <p className="text-sm text-slate-500">{selectedCustomer.phone}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearCustomer}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-500">
                      <Bike className="w-4 h-4" />
                    </span>
                  </div>
                </div>

                {/* Bike Section - Only show if customer is selected */}
                {selectedCustomer && (
                  <>
                    {/* Show bike selection if customer has bikes */}
                    {customerBikes.length > 0 && (
                      <div className="space-y-3">
                        <Label>{language === 'en' ? 'Select Vehicle' : 'বাইক নির্বাচন করুন'}</Label>
                        <div className="grid grid-cols-1 gap-2">
                          {customerBikes.map((bike) => (
                            <button
                              key={bike.id}
                              type="button"
                              onClick={() => handleSelectBike(bike)}
                              className={`text-left p-3 rounded-lg border transition-colors ${
                                selectedBikeId === bike.id 
                                  ? 'border-primary bg-primary/10' 
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <p className="font-medium">{bike.make} {bike.model}</p>
                              <div className="flex gap-2 text-sm text-slate-500">
                                {bike.year && <span>{bike.year}</span>}
                                {bike.registration_number && <span>{bike.year ? '•' : ''} {bike.registration_number}</span>}
                                {bike.color && <span>{(bike.year || bike.registration_number) ? '•' : ''} {bike.color}</span>}
                              </div>
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={handleNewBikeToggle}
                            className={`text-left p-3 rounded-lg border transition-colors ${
                              showNewBike 
                                ? 'border-primary bg-primary/10' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <p className="font-medium flex items-center justify-center gap-2">
                              <Plus className="w-4 h-4" />
                              {language === 'en' ? 'Add New Vehicle' : 'নতুন বাইক যোগ করুন'}
                            </p>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Show new bike form if no bikes or adding new */}
                    {(customerBikes.length === 0 || showNewBike) && (
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">{language === 'en' ? 'Make' : 'মেক'} *</Label>
                              <Input
                                placeholder="Yamaha, Honda..."
                                value={formData.bike_make}
                                onChange={(e) => setFormData(prev => ({ ...prev, bike_make: e.target.value }))}
                                className="h-10"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">{language === 'en' ? 'Model' : 'মডেল'} *</Label>
                              <Input
                                placeholder="FZ-S, CBR..."
                                value={formData.bike_model}
                                onChange={(e) => setFormData(prev => ({ ...prev, bike_model: e.target.value }))}
                                className="h-10"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs">{language === 'en' ? 'Year' : 'বছর'}</Label>
                              <Input
                                type="number"
                                placeholder="2024"
                                value={formData.bike_year || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, bike_year: e.target.value }))}
                                className="h-10"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">{language === 'en' ? 'Reg. Number' : 'রেজি. নম্বর'}</Label>
                              <Input
                                placeholder="LA-60-4240 or 60-4240"
                                value={formData.bike_registration}
                                onChange={(e) => setFormData(prev => ({ ...prev, bike_registration: formatRegistrationNumber(e.target.value) }))}
                                className="h-10"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">{language === 'en' ? 'Color' : 'রঙ'}</Label>
                              <Input
                                placeholder="Black"
                                value={formData.bike_color || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, bike_color: e.target.value }))}
                                className="h-10"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Status - hidden for parts-only jobs (no services, only parts) */}
        {((formData.services?.length > 0) || (formData.custom_services?.length > 0) || !(formData.parts_used?.length > 0)) && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{language === 'en' ? 'Job Status' : 'জবের স্ট্যাটাস'}</CardTitle>
            </CardHeader>
            <CardContent>
              <Select 
                value={formData.status} 
                onValueChange={handleStatusChange}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions
                    .filter(opt => {
                      // Show 'delivered' for Quick Entry jobs OR if job already has an invoice
                      if (opt.value === 'delivered') {
                        return job?.customer_name === 'Quick Entry' || hasInvoice;
                      }
                      return true;
                    })
                    .map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {language === 'bn' ? opt.labelBn : opt.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Status Backwards Confirmation Dialog */}
        <AlertDialog open={showStatusConfirm} onOpenChange={setShowStatusConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                {language === 'en' ? 'Confirm Status Change' : 'স্ট্যাটাস পরিবর্তন নিশ্চিত করুন'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {language === 'en' 
                  ? 'You are moving the job status backwards. Are you sure you want to proceed?' 
                  : 'আপনি জবের স্ট্যাটাস পিছনে নিয়ে যাচ্ছেন। আপনি কি নিশ্চিত যে আপনি এগিয়ে যেতে চান?'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={cancelStatusChange}>
                {language === 'en' ? 'Cancel' : 'বাতিল'}
              </AlertDialogCancel>
              <AlertDialogAction onClick={confirmStatusChange}>
                {language === 'en' ? 'Confirm' : 'নিশ্চিত'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Services */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              {language === 'en' ? 'Services' : 'সার্ভিস'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search Services */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={language === 'en' ? 'Search services...' : 'সার্ভিস খুঁজুন...'}
                value={serviceSearch}
                onChange={(e) => {
                  setServiceSearch(e.target.value);
                  fetchServices(e.target.value);
                }}
                className="pl-10 pr-10"
              />
              {serviceSearch && (
                <button
                  onClick={() => {
                    setServiceSearch('');
                    fetchServices('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Services Grid */}
            {availableServices.length > 0 ? (
              <div className={`grid grid-cols-2 md:grid-cols-${availableServices.length > 15 ? '4' : '3'} gap-3`}>
                  {availableServices.map((service) => (
                    <div
                      key={service.id}
                      onClick={() => handleServiceToggle(service.id)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.services.includes(service.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`service-${service.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{language === 'bn' && service.name_bn ? service.name_bn : service.name}</p>
                          <p className="text-xs text-slate-500">{service.estimated_time}</p>
                        </div>
                        <div onClick={(e) => { e.stopPropagation(); }} className="ml-2">
                          {editingServicePrice === service.id ? (
                            <Input
                              type="text"
                              value={Math.round(formData.service_prices?.[service.id] ?? service.price)}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                updateServicePrice(service.id, value);
                              }}
                              onBlur={() => setEditingServicePrice(null)}
                              onKeyDown={(e) => e.key === 'Enter' && setEditingServicePrice(null)}
                              className="w-20 h-7 text-xs"
                              autoFocus
                            />
                          ) : (
                            <button
                              onClick={() => setEditingServicePrice(service.id)}
                              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-200 transition-colors group"
                            >
                              <span className="text-sm font-bold text-blue-800">{formatCurrency(formData.service_prices?.[service.id] ?? service.price, language)}</span>
                              <Pencil className="w-3 h-3 text-slate-400 group-hover:text-blue-800 transition-colors" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
            ) : (
              <p className="text-center text-slate-500 py-4">
                {language === 'en' ? 'No services found' : 'কোন সার্ভিস পাওয়া যায়নি'}
              </p>
            )}

            {/* Custom Service (Mojuri) Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-slate-700">
                  {language === 'en' ? 'Custom Service (Mojuri)' : 'কাস্টম সার্ভিস (মজুরি)'}
                </p>
                <button
                  type="button"
                  onClick={() => setShowServiceCost(!showServiceCost)}
                  className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showServiceCost ? (language === 'en' ? 'Hide cost' : 'খরচ লুকান') : (language === 'en' ? '+ Add cost' : '+ খরচ যোগ')}
                </button>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder={language === 'en' ? 'Service name' : 'সার্ভিসের নাম'}
                  value={customService.name}
                  onChange={(e) => setCustomService(prev => ({ ...prev, name: e.target.value }))}
                  className="flex-1"
                />
                {showServiceCost && (
                  <Input
                    type="text"
                    placeholder={language === 'en' ? 'Cost' : 'খরচ'}
                    value={customService.cost}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setCustomService(prev => ({ ...prev, cost: value }));
                    }}
                    className="w-24"
                  />
                )}
                <Input
                  type="text"
                  placeholder={language === 'en' ? 'Price' : 'মূল্য'}
                  value={customService.price}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setCustomService(prev => ({ ...prev, price: value }));
                  }}
                  className="w-24"
                />
                <Button
                  type="button"
                  onClick={addCustomService}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Custom Services List */}
              {formData.custom_services && formData.custom_services.length > 0 && (
                <div className="mt-3 space-y-2">
                  {formData.custom_services.map((cs) => (
                    <div key={cs.id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-200">
                      <div>
                        <p className="font-medium text-sm">{cs.name}</p>
                        <p className="text-xs text-slate-500">{cs.cost > 0 ? `${language === 'en' ? 'Cost' : 'খরচ'}: ${formatCurrency(cs.cost, language)}` : (language === 'en' ? 'Custom' : 'কাস্টম')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-blue-800">{formatCurrency(cs.price, language)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCustomService(cs.id)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg flex justify-between">
              <span className="font-semibold">{language === 'en' ? 'Services Total' : 'সার্ভিস মোট'}</span>
              <span className="font-bold text-blue-800">{formatCurrency(totals.servicesTotal, language)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Parts */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              {t('parts_needed')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Selected Parts */}
            {formData.parts_used.length > 0 && (
              <div className="space-y-2 mb-4">
                {formData.parts_used.map((part) => (
                  <div key={part.part_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium">{part.part_name}</p>
                        {partCategoryMap[part.part_id] && !partCategoryMap[part.part_id].is_non_consumable && partCategoryMap[part.part_id].name?.toLowerCase() !== 'not consumable' && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] text-slate-400 bg-slate-50 rounded-full border border-slate-200">
                            <Bell className="w-3 h-3" />
                            {partCategoryMap[part.part_id].days || 90}d
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{formatCurrency(part.unit_price, language)} each</p>
                    </div>
                    <div className="flex items-center justify-between gap-3 flex-1">
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdatePartQuantity(part.part_id, part.quantity - 1)}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <Input
                          type="number"
                          min="1"
                          value={part.quantity}
                          onChange={(e) => handleUpdatePartQuantity(part.part_id, e.target.value)}
                          className="w-16 text-center"
                          data-testid={`part-qty-${part.part_id}`}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdatePartQuantity(part.part_id, part.quantity + 1)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Editable Total Price - Right Aligned */}
                      <div className="w-32">
                        {editingPartPrice === part.part_id ? (
                          <Input
                            type="text"
                            value={Math.round(part.quantity * part.unit_price)}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              handleUpdatePartPrice(part.part_id, value);
                            }}
                            onBlur={() => setEditingPartPrice(null)}
                            onKeyDown={(e) => e.key === 'Enter' && setEditingPartPrice(null)}
                            className="w-full h-8 text-sm"
                            autoFocus
                            data-testid={`part-price-${part.part_id}`}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setEditingPartPrice(part.part_id)}
                            className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-200 transition-colors group ml-auto"
                          >
                            <span className="font-bold text-blue-800">{formatCurrency(part.quantity * part.unit_price, language)}</span>
                            <Pencil className="w-3 h-3 text-slate-400 group-hover:text-blue-800 transition-colors" />
                          </button>
                        )}
                      </div>
                      
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemovePart(part.part_id)}
                        data-testid={`remove-part-${part.part_id}`}
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Search Parts */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={language === 'en' ? 'Search by name, SKU, brand...' : 'নাম, SKU, ব্র্যান্ড দিয়ে খুঁজুন...'}
                value={partSearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setPartSearch(val);
                  clearTimeout(window._partSearchTimer);
                  window._partSearchTimer = setTimeout(() => fetchParts(val), 300);
                }}
                className="pl-10 pr-10"
              />
              {partSearch && (
                <button
                  onClick={() => {
                    setPartSearch('');
                    fetchParts('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Parts Grid */}
            {partSearch ? (
              // Show search results
              availableParts.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {availableParts.map((part) => (
                    <Button
                      key={part.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddPart(part)}
                      className="justify-start text-left h-auto py-2"
                      data-testid={`add-part-${part.id}`}
                    >
                      <div className="truncate w-full">
                        <p className="text-xs truncate font-medium">{part.name}</p>
                        <p className="text-xs text-slate-500">{formatCurrency(part.selling_price, language)}</p>
                        <p className="text-[10px] text-slate-400 truncate">
                          {part.brand && <span>{part.brand}</span>}
                          {part.brand && part.stock_quantity != null && <span> · </span>}
                          {part.stock_quantity != null && <span>{language === 'en' ? 'Stock' : 'স্টক'}: {part.stock_quantity}</span>}
                          {partCategoryMap[part.id] && !partCategoryMap[part.id].is_non_consumable && partCategoryMap[part.id].name?.toLowerCase() !== 'not consumable' && <span>{(part.brand || part.stock_quantity != null) ? ' · ' : ''}<Bell className="inline w-[3px] h-[3px]" /> {partCategoryMap[part.id].days || 90}d</span>}
                        </p>
                      </div>
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4">
                  {language === 'en' ? 'No parts found' : 'কোন পার্টস পাওয়া যায়নি'}}
                </p>
              )
            ) : (
              // Show recent parts when no search
              recentParts.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">
                    {language === 'en' ? 'Recently used parts' : 'সম্প্রতি ব্যবহৃত পার্টস'}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {recentParts.map((part) => (
                      <Button
                        key={part.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddPart(part)}
                        className="justify-start text-left h-auto py-2"
                        data-testid={`add-part-${part.id}`}
                      >
                        <div className="truncate w-full">
                          <p className="text-xs truncate font-medium">{part.name}</p>
                          <p className="text-xs text-slate-500">{formatCurrency(part.selling_price, language)}</p>
                          <p className="text-[10px] text-slate-400 truncate">
                            {part.brand && <span>{part.brand}</span>}
                            {part.brand && part.stock_quantity != null && <span> · </span>}
                            {part.stock_quantity != null && <span>{language === 'en' ? 'Stock' : 'স্টক'}: {part.stock_quantity}</span>}
                            {partCategoryMap[part.id] && !partCategoryMap[part.id].is_non_consumable && partCategoryMap[part.id].name?.toLowerCase() !== 'not consumable' && <span>{(part.brand || part.stock_quantity != null) ? ' · ' : ''}<Bell className="inline w-[3px] h-[3px]" /> {partCategoryMap[part.id].days || 90}d</span>}
                          </p>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              )
            )}
            
            <div className="mt-4 p-3 bg-orange-50 rounded-lg flex justify-between">
              <span className="font-semibold">{language === 'en' ? 'Parts Total' : 'পার্টস মোট'}</span>
              <span className="font-bold text-orange-600">{formatCurrency(totals.partsTotal, language)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Custom Parts (not in inventory) */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              {language === 'en' ? 'Custom Parts (Not in Inventory)' : 'কাস্টম পার্টস (ইনভেন্টরিতে নেই)'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder={language === 'en' ? 'Part name' : 'পার্টসের নাম'}
                value={customPart.name}
                onChange={(e) => setCustomPart(prev => ({ ...prev, name: e.target.value }))}
                className="flex-1"
              />
              <Input
                type="text"
                placeholder={language === 'en' ? 'Buying ৳' : 'ক্রয় ৳'}
                value={customPart.buying_price}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setCustomPart(prev => ({ ...prev, buying_price: value }));
                }}
                className="w-24"
              />
              <Input
                type="text"
                placeholder={language === 'en' ? 'Selling ৳' : 'বিক্রয় ৳'}
                value={customPart.selling_price}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setCustomPart(prev => ({ ...prev, selling_price: value }));
                }}
                className="w-24"
              />
              <Button type="button" onClick={addCustomPart} size="sm" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {formData.custom_parts && formData.custom_parts.length > 0 && (
              <div className="space-y-2">
                {formData.custom_parts.map((cp) => (
                  <div key={cp.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{cp.name}</p>
                      <p className="text-xs text-slate-500">
                        {language === 'en' ? 'Buy' : 'ক্রয়'}: {formatCurrency(cp.buying_price, language)} · {language === 'en' ? 'Sell' : 'বিক্রয়'}: {formatCurrency(cp.selling_price, language)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => updateCustomPartQuantity(cp.id, -1)}>
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="font-bold w-8 text-center">{cp.quantity || 1}</span>
                        <Button type="button" size="sm" variant="outline" onClick={() => updateCustomPartQuantity(cp.id, 1)}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <span className="font-bold text-amber-800">{formatCurrency((cp.quantity || 1) * cp.selling_price, language)}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeCustomPart(cp.id)}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Technician & Notes */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{language === 'en' ? 'Assignment & Notes' : 'অ্যাসাইনমেন্ট ও নোট'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{language === 'en' ? 'Assigned Technician' : 'অ্যাসাইনড টেকনিশিয়ান'}</Label>
              <Select 
                value={formData.technician_id || "unassigned"} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, technician_id: v === "unassigned" ? "" : v }))}
              >
                <SelectTrigger data-testid="technician-select">
                  <SelectValue placeholder={language === 'en' ? 'Select technician...' : 'টেকনিশিয়ান নির্বাচন করুন...'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">{language === 'en' ? 'Unassigned' : 'অ্যাসাইন করা হয়নি'}</SelectItem>
                  {technicians.map(tech => (
                    <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{language === 'en' ? 'Expected Delivery' : 'প্রত্যাশিত ডেলিভারি'}</Label>
              <Input
                type="datetime-local"
                value={formData.expected_delivery?.slice(0, 16) || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, expected_delivery: e.target.value }))}
                data-testid="expected-delivery"
              />
            </div>
            
            <div>
              <Label>{language === 'en' ? 'Notes' : 'নোট'}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={language === 'en' ? 'Add any notes about this job...' : 'এই জব সম্পর্কে কোন নোট যোগ করুন...'}
                rows={3}
                data-testid="job-notes"
              />
            </div>
          </CardContent>
        </Card>

        {/* Total & Save */}
        <Card className="bg-slate-900 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400">{language === 'en' ? 'Total Amount' : 'মোট পরিমাণ'}</p>
                <p className="text-4xl font-bold">{formatCurrency(totals.total, language)}</p>
              </div>
              <Button 
                onClick={handleSave}
                disabled={saving}
                size="lg"
                className="bg-green-600 hover:bg-green-700"
                data-testid="save-job-btn"
              >
                {saving ? <Spinner size="sm" /> : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    {language === 'en' ? 'Save Changes' : 'পরিবর্তন সংরক্ষণ করুন'}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
